import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Calendar, CheckCircle2, Clock, FileText, Loader2, Mail, Phone, Plane, Upload, User } from "lucide-react";
import { API } from "@/lib/api";

// The driver/staff member's personal live list — only their assigned jobs, plus the client's contact.
export default function DriverView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/drive/${token}`)
      .then((res) => {
        setData(res.data);
        setInvoices(res.data.invoices || []);
      })
      .catch(() => setError("This list is not available — the link may have been revoked."));
  }, [token]);

  const uploadInvoice = async () => {
    if (!file || uploading) return;
    setUploading(true);
    setUploadMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/drive/${token}/invoice`, fd);
      setInvoices((ls) => [...ls, res.data.invoice]);
      setFile(null);
      setUploadMsg("Received — thank you. We'll take it from here.");
    } catch (e) {
      setUploadMsg(e.response?.data?.detail || "Upload failed — try again.");
    } finally {
      setUploading(false);
    }
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070709] px-6 text-center text-neutral-400">
        <p data-testid="driver-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070709]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
      </div>
    );
  }

  const fmtDay = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "");
  // group jobs by their real date (fallback: day number)
  const groups = [];
  for (const j of data.jobs) {
    const key = j.date || `day-${j.day}`;
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.jobs.push(j);
    else groups.push({ key, label: j.date ? fmtDay(j.date) : `Day ${j.day}`, jobs: [j] });
  }

  return (
    <div data-testid="driver-page" className="min-h-screen bg-[#070709] text-neutral-100">
      <div className="mx-auto max-w-xl px-5 py-10 sm:py-14">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-center text-[10px] font-semibold uppercase tracking-[0.4em] text-amber-400/80">Exclusive Reykjavik</p>
          <h1 className="mt-3 text-center font-luxe text-2xl uppercase tracking-[0.15em] text-neutral-50 sm:text-3xl" data-testid="driver-trip-name">
            {data.trip_name}
          </h1>
          <p className="mt-2 text-center text-xs uppercase tracking-[0.25em] text-neutral-500">
            For {data.driver}
            {data.arrival_date ? ` · ${fmtDay(data.arrival_date)} → ${fmtDay(data.departure_date)}` : ""}
          </p>
        </motion.div>

        {(data.client?.name || data.client?.phone || data.client?.email) && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="driver-client-card"
            className="mt-8 border border-amber-500/20 bg-[#0c0c10] p-5"
          >
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">Your client</p>
            <p className="mt-2 flex items-center gap-2 text-base text-neutral-100">
              <User className="h-4 w-4 text-amber-400/70" /> {data.client.name}
            </p>
            <div className="mt-2 space-y-1.5">
              {data.client.phone && (
                <a data-testid="driver-client-phone" href={`tel:${data.client.phone}`} className="flex items-center gap-2 text-sm text-amber-200/90 transition-colors hover:text-amber-100">
                  <Phone className="h-3.5 w-3.5" /> {data.client.phone}
                </a>
              )}
              {data.client.email && (
                <a data-testid="driver-client-email" href={`mailto:${data.client.email}`} className="flex items-center gap-2 text-sm text-amber-200/90 transition-colors hover:text-amber-100">
                  <Mail className="h-3.5 w-3.5" /> {data.client.email}
                </a>
              )}
            </div>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="mt-8">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">Your jobs — always up to date</p>
          {groups.length === 0 && (
            <p data-testid="driver-empty" className="mt-4 border border-dashed border-neutral-700 px-4 py-6 text-center text-sm text-neutral-500">
              Nothing assigned to you yet — this list fills in as jobs get your name.
            </p>
          )}
          {groups.map((g, gi) => (
            <div key={g.key} data-testid={`driver-day-${gi}`} className="mt-6">
              <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-200/90">
                <Calendar className="h-3.5 w-3.5" /> {g.label}
              </p>
              <div className="mt-2 space-y-2">
                {g.jobs.map((j, ji) => (
                  <div key={ji} data-testid={`driver-job-${gi}-${ji}`} className="flex gap-4 border border-neutral-800/80 bg-[#0c0c10] px-4 py-3.5">
                    <span className="flex w-14 shrink-0 items-start gap-1 pt-0.5 font-luxe text-base tracking-wide text-amber-100">
                      <Clock className="mt-1 h-3.5 w-3.5 text-amber-400/60" />
                      {j.time || "—"}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-neutral-100">{j.title}</span>
                      {(j.pickup_loc || j.dropoff_loc) && (
                        <span data-testid={`driver-job-route-${gi}-${ji}`} className="mt-0.5 block text-xs leading-relaxed text-amber-200/80">
                          {j.pickup_loc || "?"}
                          {j.dropoff_loc ? ` → ${j.dropoff_loc}` : ""}
                        </span>
                      )}
                      {j.details && <span className="mt-0.5 block text-xs leading-relaxed text-neutral-400">{j.details}</span>}
                      {j.end_time && (
                        <span data-testid={`driver-job-backby-${gi}-${ji}`} className="mt-1 inline-block border border-amber-500/30 bg-amber-950/20 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-amber-200/90">
                          Back by {j.end_time}
                        </span>
                      )}
                      {j.flight && (
                        <a
                          data-testid={`driver-job-flight-${gi}-${ji}`}
                          href={`https://www.flightaware.com/live/flight/${encodeURIComponent(j.flight)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1.5 inline-flex items-center gap-1.5 border border-sky-500/40 bg-sky-950/30 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-sky-200 transition-colors hover:border-sky-300 hover:text-sky-100"
                        >
                          <Plane className="h-3 w-3" /> {j.flight} — live status
                        </a>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          data-testid="driver-invoice-card"
          className="mt-10 border border-amber-500/20 bg-[#0c0c10] p-5"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">Invoice us — right from here</p>
          {data.billing && (
            <div className="mt-3 border border-neutral-800/80 bg-black/30 px-4 py-3">
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Make your invoice out to</p>
              <p className="mt-1 text-sm font-semibold text-neutral-100" data-testid="driver-billing-name">{data.billing.name}</p>
              <p className="text-xs text-neutral-400" data-testid="driver-billing-kt">Kennitala {data.billing.kt}</p>
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <label
              data-testid="driver-invoice-choose"
              className="flex cursor-pointer items-center gap-2 border border-neutral-700 px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              <FileText className="h-3.5 w-3.5" />
              {file ? file.name : "Choose photo or PDF"}
              <input
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null);
                  setUploadMsg("");
                }}
              />
            </label>
            <button
              data-testid="driver-invoice-upload"
              disabled={!file || uploading}
              onClick={uploadInvoice}
              className="flex items-center gap-2 border border-amber-500/50 px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-40"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Send invoice
            </button>
          </div>
          {uploadMsg && (
            <p data-testid="driver-invoice-msg" className={`mt-3 text-xs ${uploadMsg.startsWith("Received") ? "text-emerald-300" : "text-red-300"}`}>
              {uploadMsg}
            </p>
          )}
          {invoices.length > 0 && (
            <div className="mt-4 space-y-1.5">
              {invoices.map((inv) => (
                <p key={inv.invoice_id} data-testid={`driver-invoice-${inv.invoice_id}`} className="flex items-center gap-2 text-xs text-neutral-400">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400/80" />
                  {inv.filename}
                  {inv.uploaded_at ? ` — sent ${new Date(inv.uploaded_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}
                </p>
              ))}
            </div>
          )}
        </motion.div>

        <p className="mt-12 text-center text-[10px] uppercase tracking-[0.3em] text-neutral-600">
          <Link to="/" className="transition-colors hover:text-amber-200">Exclusive Reykjavik · Key to the city — and beyond</Link>
        </p>
      </div>
    </div>
  );
}
